import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BaseComponent } from '../../core/base.component';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AccountRoleModel } from '../../api/models/account-role-model';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { AccountService } from '../account.service';
import { NotificationService } from '../../notification/notification.service';
import { ModuleDefinition, PermissionDefinition, RolesDefinition } from '../../core/security/role-definition';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { AccountRoleItemModel } from '../../api/models/account-role-item-model';
import { MenuGroupEnum } from '../../core/security/menu-group.enum';
import { ModulesEnum } from '../../core/security/modules.enum';
import { CurrentUser, LookupModel } from '../../api/models';
import { MatConfirmDialogComponent } from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ExtensionsSelectService } from '../../editors/extensions-select.service';
import { MatPbxAccountsSelectDialogComponent } from '../../editors/mat-pbx-account-select-dialogs/mat-pbx-accounts-select-dialog.component';
import * as _ from 'lodash';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { NgIf, NgFor } from '@angular/common';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { FlexModule } from '@angular/flex-layout';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatMultiselectComponent } from '../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatIcon } from '@angular/material/icon';
import { ModulesFilterPipe } from '../../core/pipes/modules-filter.pipe';

export const permissionWeights = {
  View: 1,
  Edit: 2,
  Delete: 3,
};

@Component({
  selector: 'app-edit',
  templateUrl: './role-edit.component.html',
  styleUrls: ['./role-edit.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    NgIf,
    MatTextInputComponent,
    MatAccordion,
    NgFor,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    FlexModule,
    MatRadioGroup,
    MatRadioButton,
    MatMultiselectComponent,
    MatCheckboxInputComponent,
    MatButton,
    MatTooltip,
    Angular2PromiseButtonModule,
    MatIcon,
    ModulesFilterPipe,
  ],
})
export class RoleEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  buttonSubmitPromise: Promise<void>;
  editModel: AccountRoleModel = { id: null, items: [] };
  modules: ModuleDefinition[] = [];
  tabs = [];
  filteredTabs = [];

  excludeExtensions = [];
  excludeExcludedExtensions = [];
  excludeRecordingExtensions = [];
  excludeRecordingExcludedExtensions = [];

  userPermissionWeightsByPrefix: Record<string, number> = {};

  public id: string;
  public isClone = false;

  private paramsSubscription;

  departmentsLookup: any[] = [];
  departmentsLookupList: LookupModel[] = [];

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    public reportsService: ExtensionsSelectService,
    private accountService: AccountService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.id = this.route.snapshot.params['id'];
    this.isClone = this.route.snapshot.queryParams['clone'] === 'true';

    this.headerService.showBackButton(true);
    this.createForm();

    this.editRoles = [RolesEnum.AccountRoles_Edit, RolesEnum.AccountRoles_Delete];
    this.deleteRoles = [RolesEnum.AccountRoles_Delete];
  }

  calculateRoleWeights(roleItems: string[]): Record<string, number> {
    const weightsByPrefix: Record<string, number> = {};
    for (const item of roleItems) {
      const [prefix, permissionType] = item.split('.');
      weightsByPrefix[prefix] = (weightsByPrefix[prefix] || 0) + (permissionWeights[permissionType] || 0);
    }
    return weightsByPrefix;
  }

  getFormControl(controlName: string): UntypedFormControl {
    return this.form.get(controlName) as UntypedFormControl;
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    this.userPermissionWeightsByPrefix = this.calculateRoleWeights(user?.roles || []);

    if (user) {
      this.refreshLookupData(user).then(() => {});
    } else {
      this.departmentsLookup = [];
      this.departmentsLookupList = [];
    }

    console.log('userPermissionWeightsByPrefix', this.userPermissionWeightsByPrefix);

    this.tabs = [
      {
        title: 'Basic SMS',
        group: MenuGroupEnum.MessagingGroup,
        visible: this.authService.userHasPackage([ModulesEnum.MessagingModule]),
      },
      {
        title: 'Pro Business SMS',
        group: MenuGroupEnum.ProMessagingGroup,
        visible: this.authService.userHasPackage([
          ModulesEnum.ProMessagingModule,
          ModulesEnum.ProMessagingReportsModule,
          ModulesEnum.MessagingAutomationModule,
          ModulesEnum.MessagingTemplateModule,
        ]),
      },
      {
        title: 'My Call Logs',
        group: MenuGroupEnum.CallLogsGroup,
        visible: this.authService.userHasPackage([ModulesEnum.BasicCallLogsModule]),
      },
      {
        title: 'Analytics',
        group: MenuGroupEnum.ReportsGroup,
        visible: this.authService.userHasPackage([
          ModulesEnum.ActivityDashboardModule,
          ModulesEnum.CallLogsModule,
          ModulesEnum.GroupsDashboardModule,
          ModulesEnum.GroupsSummaryModule,
          ModulesEnum.UsersSummaryModule,
          ModulesEnum.ReportsModule,

          ModulesEnum.ActivityDashboardV2Module,
          ModulesEnum.CallLogsV2Module,
          ModulesEnum.GroupsSummaryV2Module,
          ModulesEnum.UsersSummaryV2Module,
        ]),
      },
      {
        title: 'Recordings',
        group: MenuGroupEnum.RecordingsGroup,
        visible: this.authService.userHasPackage([ModulesEnum.RecordingsModule]),
      },
      {
        title: 'Phone Numbers',
        group: MenuGroupEnum.PhoneNumbersGroup,
        visible: this.authService.userHasPackage([ModulesEnum.PhoneNumbersModule]),
      },
      /*{
        title: 'IP Whitelist',
        group: MenuGroupEnum.FirewallGroup,
        visible: this.authService.userHasPackage([ModulesEnum.CustomFieldsModule])
      },*/
      {
        title: 'Fax',
        group: MenuGroupEnum.FaxGroup,
        visible: this.authService.userHasPackage([ModulesEnum.FaxModule, ModulesEnum.FaxReportModule]),
      },
      {
        title: 'Pbx',
        group: MenuGroupEnum.PbxGroup,
        visible: this.authService.userHasPackage([
          ModulesEnum.PbxModule,
          ModulesEnum.PbxHolidayGreetingsModule,
          ModulesEnum.PbxAccountsModule,
          ModulesEnum.FirewallModule,
        ]),
      },
      {
        title: 'Contacts',
        group: MenuGroupEnum.ContactGroup,
        visible: this.authService.userHasPackage([ModulesEnum.ContactModule, ModulesEnum.PrivateContactModule]),
      },
      {
        title: 'Tags',
        group: MenuGroupEnum.TagsGroup,
        visible: this.authService.userHasPackage([ModulesEnum.TagsModule]),
      },
      {
        title: 'Custom Fields',
        group: MenuGroupEnum.CustomFieldsGroup,
        visible: this.authService.userHasPackage([ModulesEnum.CustomFieldsModule]),
      },
      {
        title: 'Events Flow',
        group: MenuGroupEnum.EventsFlowGroup,
        visible: this.authService.userHasPackage([ModulesEnum.EventsFlowModule]),
      },
      {
        title: 'TeleGo SoftPhone Apps',
        group: MenuGroupEnum.SoftPhoneGroup,
        visible: this.authService.userHasPackage([ModulesEnum.SoftPhoneModule, ModulesEnum.BasicSoftPhoneModule]),
      },
      {
        title: 'HHAeXchange',
        group: MenuGroupEnum.HhaGroup,
        visible: this.authService.userHasPackage([ModulesEnum.HhaModule]),
      },
      {
        title: 'Settings',
        group: MenuGroupEnum.SettingsGroup,
        visible: this.authService.userHasPackage([
          ModulesEnum.AccountRolesModule,
          ModulesEnum.AccountSettingsModule,
          ModulesEnum.AccountStorageModule,
          ModulesEnum.AccountUsersModule,
          ModulesEnum.AuditModule,
          ModulesEnum.UsersModule,
        ]),
      },
    ];

    this.filteredTabs = this.tabs.filter(x => x.visible);
  }

  async refreshLookupData(user: CurrentUser) {
    if (user.isAdmin) {
      await this.reportsService.pbxAccountLookup();
      this.departmentsLookup = this.reportsService.departmentsLookup;
      this.departmentsLookupList = this.reportsService.departmentsLookupList;
    } else {
      this.departmentsLookup = _.union(user.reportingDepartments || [], user.recordingDepartments || []);
      this.departmentsLookupList = this.departmentsLookup.map(x => {
        return {
          id: x,
          text: x,
        };
      });
    }
  }

  createForm(): void {
    const controlsConfig: any = {
      name: ['', Validators.required],
      description: [''],
    };

    this.modules.forEach(module => {
      controlsConfig[module.permissionPrefix] = ['none'];

      if (module.additionalPermissions) {
        module.additionalPermissions.forEach(item => {
          let defValue: any = '';
          if (
            item.inputType === 'PbxExtensionsInputComponent' ||
            item.inputType === 'PbxGroupsInputComponent' ||
            item.inputType === 'PbxDepartmentsInputComponent' ||
            item.inputType === 'PbxOtherAccountsInputComponent'
          ) {
            defValue = [];
          }

          if (item.inputType === 'CheckboxInputComponent') {
            defValue = false;
          }

          controlsConfig[item.inputName] = [defValue];
        });
      }
    });

    this.form = this.fb.group(controlsConfig);
  }

  async onSubmit() {
    this.serverError = false;
    if (this.form.valid && this.hasEditAccess) {
      this.submitted = true;
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit() {
    const reqData = this.prepareFormModel();

    try {
      await this.accountService.saveAccountRole(reqData);
      this.notificationService.notifySuccess('Role updated successfully');
      this.ngOnChanges();
      this.headerService.goBack();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): AccountRoleModel {
    const formModel = this.form.value;
    const permissions: AccountRoleItemModel[] = [];

    this.modules.forEach(module => {
      const perm = formModel[module.permissionPrefix];
      if (perm && perm !== 'none' && module.permissionPrefix.indexOf('_') === -1) {
        permissions.push({
          permissionKey: perm,
        });
      }

      if (module.additionalPermissions) {
        module.additionalPermissions.forEach(item => {
          const additionalPerm = formModel[item.inputName];

          let defValue: any = additionalPerm;
          if (
            item.inputType === 'PbxExtensionsInputComponent' ||
            item.inputType === 'PbxGroupsInputComponent' ||
            item.inputType === 'PbxDepartmentsInputComponent' ||
            item.inputType === 'PbxOtherAccountsInputComponent'
          ) {
            defValue = additionalPerm.length > 0 ? additionalPerm.filter(x => !!x).join(', ') : '';
          }

          if (item.inputType === 'CheckboxInputComponent') {
            if (defValue) {
              permissions.push({
                permissionKey: item.inputName.replace('_', '.'),
              });
            }
          } else {
            permissions.push({
              permissionKey: item.inputName.replace('_', '.'),
              specialPermission: defValue,
            });
          }
        });
      }
    });

    return {
      id: this.editModel.id,
      name: formModel.name,
      description: formModel.description,
      items: permissions,
    };
  }

  async ngOnInit() {
    super.ngOnInit();

    this.onDeleteProcess = this.onDeleteProcess.bind(this);
    this.editModel = await this.accountService.getRoleById(this.id);

    if (this.isClone) {
      this.editModel.id = 'new';
      this.editModel.name = '';
      this.editModel.isSystem = false;
    }

    const currentUser = this.authService.currentUser.getValue();
    this.modules = RolesDefinition.userModules(currentUser.modules, this.editModel.items);

    console.log('modules', this.modules);

    this.createForm();
    this.ngOnChanges();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    const defValues: any = {
      name: this.editModel.name,
      description: this.editModel.description,
    };

    this.modules.forEach(module => {
      if (module.permissions) {
        const selectedPermission = module.permissions.find(x => {
          return x.selected;
        });

        defValues[module.permissionPrefix] = selectedPermission ? selectedPermission.permissionKey : 'none';
      }

      if (module.additionalPermissions) {
        module.additionalPermissions.forEach(item => {
          let defValue: any = item.value;
          if (
            item.inputType === 'PbxExtensionsInputComponent' ||
            item.inputType === 'PbxGroupsInputComponent' ||
            item.inputType === 'PbxDepartmentsInputComponent' ||
            item.inputType === 'PbxOtherAccountsInputComponent'
          ) {
            defValue = item.value ? item.value.split(',').map(x => x.trim()) : [];

            if (item.inputName === 'Reports_Extensions') {
              this.excludeExtensions = [...defValue];
            }
            if (item.inputName === 'Reports_Exclude_Extensions') {
              this.excludeExcludedExtensions = [...defValue];
            }
            if (item.inputName === 'Recordings_Extensions') {
              this.excludeRecordingExtensions = [...defValue];
            }
            if (item.inputName === 'Recordings_Exclude_Extensions') {
              this.excludeRecordingExcludedExtensions = [...defValue];
            }
          }

          if (item.inputType === 'CheckboxInputComponent') {
            defValues[item.inputName] = Boolean(defValue);
          } else {
            defValues[item.inputName] = defValue;
          }
        });
      }
    });

    this.form.reset(defValues);
    this.form.markAllAsTouched();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  async onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Delete Role',
        text: 'Are you sure you want to delete role?',
        actionTitle: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.onDeleteProcess();
      }
    });
  }

  async onDeleteProcess(): Promise<void> {
    await this.accountService.deleteAccountRole(this.editModel.id);
    await this.notificationService.notifySuccess('Role deleted successfully');
    await this.headerService.goToComponent('RolesComponent');
  }

  onPbxAccounts(
    $event: MouseEvent,
    formControl: UntypedFormControl,
    accounts: any[],
    title: string,
    cardTitle: string,
    excludeValues: any[],
  ) {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(MatPbxAccountsSelectDialogComponent, {
      width: '500px',
      data: {
        selectedAccounts: formControl.value || [],
        accounts: accounts,
        title: title,
        cardTitle: cardTitle,
        excludeValues: excludeValues,
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        formControl.patchValue(result);
      }
    });
  }

  onPbxExtensionsInputValueChange($event: any, inputName: any) {
    console.log($event, inputName);

    if (inputName === 'Reports_Extensions') {
      this.excludeExtensions = [...$event];
    }
    if (inputName === 'Reports_Exclude_Extensions') {
      this.excludeExcludedExtensions = [...$event];
    }

    if (inputName === 'Recordings_Extensions') {
      this.excludeRecordingExtensions = [...$event];
    }
    if (inputName === 'Recordings_Exclude_Extensions') {
      this.excludeRecordingExcludedExtensions = [...$event];
    }
  }

  hasPermissionToEnable(permission: PermissionDefinition): boolean {
    const user = this.authService.currentUser.getValue();
    const [prefix, permissionType] = permission.permissionKey.split('.');
    const permissionWeight = permissionWeights[permissionType] || 0;

    return (
      permission.permissionKey === 'none' ||
      user.isAdmin ||
      this.userPermissionWeightsByPrefix[prefix] >= permissionWeight
    );
  }

  hasSpecialPermissionToEnable(permission: PermissionDefinition): boolean {
    const user = this.authService.currentUser.getValue();
    if (user.isAdmin) {
      return true;
    }

    const [prefix, permissionType] = permission.permissionKey.split('.');

    if (!permissionWeights[permissionType]) {
      return user.roles.includes(permission.permissionKey as any);
    }
    return false;
  }
}
